import { Component, inject, signal, viewChild, ElementRef, computed } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { Workshop } from '@core/models/workshop';
import { MdbStickyModule } from 'mdb-angular-ui-kit/sticky';
import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  getWorkshopSeatBadgeClass,
  getWorkshopStatusClass,
  getFriendlyStatus,
  shouldShowSeats,
  formatWorkshopLocation,
  formatWorkshopMode,
  formatWorkshopDateTime,
  formatWorkshopTime
} from '../../utilities/workshop.util';

import { MockRecaptcha } from '../mock-recaptcha/mock-recaptcha';
import { Gradientsegmentbar } from '../gradientsegmentbar/gradientsegmentbar';
import { WorkshopService } from '@core/services/workshop';
import { MdbTooltipModule } from 'mdb-angular-ui-kit/tooltip';

export interface WorkshopModalResult {
  action: 'registered' | 'cancelled';
  success: boolean;
  message: string;
  accommodationNote?: string;
}

@Component({
  selector: 'app-workshopmodal',
  templateUrl: './workshopmodal.html',
  styleUrl: './workshopmodal.scss',
  standalone: true,
  imports: [MdbRippleModule, MdbStickyModule, TitleCasePipe, CommonModule, ReactiveFormsModule, MdbTooltipModule, MockRecaptcha, Gradientsegmentbar]
})
export class WorkshopModal {
  modalRef = inject(MdbModalRef<WorkshopModal>);
  //workshop endpoints
  private workshopService = inject(WorkshopService);
  workshop: Workshop | null = null;
  mode: 'register' | 'registered' | 'full' | 'cancelled' = 'register';

  //TODO: update styling to warning like the login screen

  // Placeholder: will be set based on whether the cancellation window has passed
  cancellationCutoffPassed = signal(false);
  firstAttemptRegister = signal(true);
  protected submitAttempted = signal(false);
  protected captchaVerified = signal(false);
  result: WorkshopModalResult | null = null;

  registerButtonText = computed(() => this.firstAttemptRegister() ? 'Register' : 'Confirm Registration'); confirmSection = viewChild<ElementRef>('confirmSection');
  cancellationSection = viewChild<ElementRef>('cancellationSection');

  // Registration confirmation form
  confirmForm = new FormGroup({
    accommodationNote: new FormControl(''),
  });

  get accommodationNote(): FormControl {
    return this.confirmForm.get('accommodationNote') as FormControl;
  }



  // Cancellation confirmation
  cancellationAck = new FormControl(false, Validators.requiredTrue);
  //#region Util methods
  //Allow for use within template
  protected getWorkshopStatusClass = getWorkshopStatusClass;
  protected getWorkshopSeatBadgeClass = getWorkshopSeatBadgeClass;
  protected getWorkshopFriendlyStatus = getFriendlyStatus;
  protected shouldShowSeats = shouldShowSeats;
  protected formatWorkshopLocation = formatWorkshopLocation;
  protected formatWorkshopMode = formatWorkshopMode;
  protected formatWorkshopDateTime = formatWorkshopDateTime;
  protected formatWorkshopTime = formatWorkshopTime;
  //
  submitRegistration(): void {
    this.firstAttemptRegister.set(false);
    this.submitAttempted.set(true);
    this.confirmForm.markAllAsTouched();
    if (!this.captchaVerified() || this.confirmForm.invalid) {
      this.confirmSection()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    this.workshopService.register(this.workshop!.workshopSessionId, this.accommodationNote.value).subscribe({
      next: () => {
        this.result = {
          action: 'registered',
          success: true,
          message: 'You have been registered for this workshop.',
          accommodationNote: this.accommodationNote.value
        };
        this.modalRef.close();
      },
      error: (err) => {
        this.result = {
          action: 'registered',
          success: false,
          message: err?.error?.message ?? 'Registration failed. Please try again.'
        };
        this.modalRef.close();
      }
    });
  }
  submitCancellation(): void {
    this.cancellationAck.markAsTouched();
    this.submitAttempted.set(true);
    if (!this.captchaVerified() || this.cancellationAck.invalid) {
      this.cancellationSection()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    this.workshopService.cancel(this.workshop!.workshopSessionId).subscribe({
      next: () => {
        this.result = {
          action: 'cancelled',
          success: true,
          message: 'Your registration has been cancelled.'
        };
        this.modalRef.close();
      },
      error: (err) => {
        this.result = {
          action: 'cancelled',
          success: false,
          message: err?.error?.message ?? 'Cancellation failed. Please try again.'
        };
        this.modalRef.close();
      }
    });
  }
}
